import { useQuery } from '@tanstack/react-query'
import useAxios from './useAxios'
import useUserManager from '../auth/useUserManager'
import { useAuthState } from '../auth/context'
import config from 'config'

export const SWITCH_PERSONA_KEYS = {
    USER_INFO: 'SWITCH_PERSONA_USER_INFO',
    ASSOCIATED_LOGINS: 'SWITCH_PERSONA_ASSOCIATED_LOGINS',
    PERSON: 'SWITCH_PERSONA_PERSON',
}

/** @param {string} [path] Path after `API_URL`, default `'/api/user'`. E.g. `'/api/v1/me/user'`. */
export const useUserInfo = (path = '/api/user') => {
    const userManager = useUserManager()
    const fetchUrl = `${config.API_URL}${path}`

    return useQuery(
        [SWITCH_PERSONA_KEYS.USER_INFO, fetchUrl],
        async () => {
            const user = await userManager.getUser()
            const rawResponse = await fetch(fetchUrl, {
                headers: {
                    Authorization: `Bearer ${user?.access_token}`,
                },
            })
            const response = await rawResponse.json()
            return response
        },
        {
            staleTime: Infinity,
        },
    )
}

/** @param {string} [associatedLoginsPath] e.g. `'/api/v1/people/AssociatedLogins'`. Default `'/api/people/AssociatedLogins'`. */
export const useAssociatedLogins = (
    associatedLoginsPath = '/api/people/AssociatedLogins',
) => {
    const callApi = useAxios()
    const [{ currentUserId }] = useAuthState()
    return useQuery(
        [
            SWITCH_PERSONA_KEYS.ASSOCIATED_LOGINS,
            currentUserId,
            associatedLoginsPath,
        ],
        () =>
            callApi({
                method: 'GET',
                url: `${associatedLoginsPath}?skip=0&take=1`,
            }).then((data) => data.data),
        {
            staleTime: Infinity,
            enabled: Boolean(currentUserId),
        },
    )
}

/** @typedef {'query' | 'route'} PersonLookupParamType */

/**
 * @param {*} personId
 * @param {string} [personLookupPath] Base GET path: with `query`, appends `?personId=`; with `route`, appends `/{personId}`.
 * @param {PersonLookupParamType} [paramType] `'query'` (default) or `'route'` for paths like `/identities/{personId}`.
 */
export const useSwitchPersonaPerson = (
    personId,
    personLookupPath = '/api/people',
    paramType = 'query',
) => {
    const callApi = useAxios()
    const id = personId != null ? String(personId) : ''
    const encodedId = encodeURIComponent(id)
    const url =
        paramType === 'route'
            ? `${personLookupPath.replace(/\/$/, '')}/${encodedId}`
            : `${personLookupPath}?personId=${encodedId}`

    return useQuery(
        [
            SWITCH_PERSONA_KEYS.PERSON,
            personId,
            personLookupPath,
            paramType,
        ],
        () =>
            callApi({
                method: 'GET',
                url,
            }).then((data) => data.data),
        {
            staleTime: Infinity,
            enabled: Boolean(personId),
        },
    )
}
